import { useBoundStore } from '@Store/index';
import { handleLoginResponse } from '@Services/init/handleLoginResponse.ts';
import { routesModel } from '@Router/router.consts.ts';
import { router } from '@Routes/__root.tsx';
import { EnvironmentVariables, getEnvironmentVariable } from '@Services/env/environment.service.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { NAVIGATION_STACK_VERSION } from '@Types/consts.ts';
import { reArrangeNavigationStack, applyCountryEddToNavigationStack } from '@Services/init/util.ts';
import { isDeclinedAuthorizedUserOnboardingStatus } from '@Utils/onboardingStatus';
import { redirectToWebAppLoginPage } from '../../utils';
import { getNavigationStackMap } from '../../consts/navigation-stack-map.ts';

interface BuildNavigationProps {
  shouldAvoidPickUpWhereLeft?: boolean;
  shouldAvoidNavigation?: boolean;
  shouldHandleLoginMannually?: boolean;
  onResume?: () => void;
}
export const buildNavigationStack = async ({
  shouldAvoidPickUpWhereLeft,
  shouldAvoidNavigation,
  shouldHandleLoginMannually,
  onResume,
}: BuildNavigationProps) => {
  const { setNavigationStack } = useBoundStore.getState().onboardingSlice;
  const { emailParam } = useBoundStore.getState().onboardingSlice;
  const {
    onboardingStatus,
    customerData: { lastStepData, onboardingTypeEnum },
  } = useBoundStore.getState().customer;
  const navigationStack = getNavigationStackMap();
  setNavigationStack(navigationStack);
  applyCountryEddToNavigationStack();
  const onboardingIdentifier = useBoundStore.getState().onboardingSlice.onboardingFlowIdentifier;
  const loginToken = useBoundStore.getState().onboardingSlice.loginToken;
  // A cookie-authenticated resume (webapp redirect) has no URL token but a valid session, so it
  // must also run the pick-up-where-you-left path.
  const cookieSessionResume = useBoundStore.getState().onboardingSlice.cookieSessionResume;
  const shouldHandleLogin =
    useBoundStore.getState().onboardingSlice.getIsEmbedded() ||
    !!loginToken ||
    !!cookieSessionResume;

  if (
    onboardingIdentifier === OnboardingFlowIdentifier.AUTHORIZED_USER &&
    !shouldAvoidPickUpWhereLeft
  ) {
    const authLastStep = useBoundStore.getState().authOnboardingSlice.lastStepData;
    const authUserOnboardingStatus =
      useBoundStore.getState().authOnboardingSlice.authorizedUserOnboardingStatus;

    if (isDeclinedAuthorizedUserOnboardingStatus(authUserOnboardingStatus)) {
      router.navigate({ to: routesModel[RoutesMap.VERIFICATION_FAILED].path });
    } else if (
      loginToken &&
      authLastStep &&
      Number(authLastStep.version) >= NAVIGATION_STACK_VERSION
    ) {
      await reArrangeNavigationStack(authLastStep.stepName);
    } else {
      router.navigate({ to: routesModel[navigationStack[0]].path });
    }
  } else if ((shouldHandleLogin || shouldHandleLoginMannually) && !shouldAvoidPickUpWhereLeft) {
    await handleLoginResponse({ onboardingStatus, onboardingTypeEnum, lastStepData, onResume });
  } else if (!shouldAvoidNavigation) {
    if (getEnvironmentVariable(EnvironmentVariables.VITE_SHOULD_JUMP_TO_CUSTOM_PAGE) == 'true') {
      router.navigate({ to: routesModel[RoutesMap.EDD_ADDITIONAL_BUSINESS_LOCATIONS].path });
    } else if (window.location.href.includes(routesModel[RoutesMap.LOGIN].path)) {
      if (onboardingIdentifier === OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS) {
        return;
      }
      redirectToWebAppLoginPage(emailParam);
    } else if (onboardingIdentifier === OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS) {
      if (window.location.pathname.includes('/multi/intro')) {
        router.navigate({ to: routesModel[navigationStack[0]].path });
      }
      return;
    } else {
      router.navigate({ to: routesModel[navigationStack[0]].path });
    }
  }
};
